/**
 * DropdownView — the SSR finder's DOM (ModelFinderView for the start-page combobox stack).
 *
 * **Positional combobox stack (ADR-0010, spec §3.3/§3.5).** Same positional
 * dumb-shell contract as `ButtonColumnView`, vertical instead of a horizontal
 * track: the server ships only position 0 (the manufacturer field) + a
 * `<template>`; `renderPlan` clones/removes real fields at the tail by plan
 * LENGTH, swaps options + label in place, and resets transient state. The
 * `DropdownEnhancer` is keyed by POSITION. No level policy, no `data-role` reveal
 * list. SSR has no geometry-dependent layout, so `layout`/`scrollActiveIntoView`
 * are no-ops.
 *
 * **Unified field component (spec §5).** The per-field `<select>` SSoT lifecycle
 * (enhance/populate/reset/applyLabel) AND the model-field image+CTA mount live in
 * the shared `FieldFinder` collaborator, composed by both finder views. This view
 * keeps only the homepage-specific concerns: the decorative hero (a static slot,
 * never touched), the single standalone CTA button, and the headline formatting.
 *
 * **The model image is part of the MODEL FIELD** (the deepest field), revealed by
 * `showCta` into that field's own detail box — never a fixed bottom slot and never
 * after the first row (that geometry was regression (a)). The decorative hero is
 * decoupled from `showCta` and always visible (that decoupling fixes regression (b)).
 *
 * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/research/unified-field-template-and-finder.md §5/§6/§7
 * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/spec.md §3.3/§3.5
 */

import { DropdownEnhancer } from '../../../src-components/dropdown/dropdown-enhancer';
import { LoadingStateManager } from '../model-finder-common';
import type { Level } from '../domain/levels';
import { type NodeId, type Position, parseNodeId, position as positionOf } from '../domain/brand';
import type { Node } from '../data/wire';
import type { CtaData, LevelLabel } from './view';
import { PositionalShellView } from './positional-shell-view';
import { FieldFinder } from './field-finder';
import { ScrollInView, nearestScrollContainer } from './scroll-in-view';
import { ScrollIntentArbiter } from './scroll-intent-arbiter';
import { bindWindowScrollIntent } from './window-scroll-intent';

/** The model-field reveal margin (px) — keeps the revealed field off the very viewport edge. */
const SCROLL_REVEAL_MARGIN = 16;

/** The open-list reveal margin (px) — gap kept between the open list edge and the viewport edge. */
const LIST_REVEAL_MARGIN = 16;

/**
 * The shared open-list reveal for both MF dropdown surfaces (ADR-0010 D39): the
 * enhancer makes the open list IN-FLOW + fit-content (so the scroll container grows
 * to it), then calls this hook to MINIMALLY scroll the whole list into view — reusing
 * the SAME `ScrollInView` machinery (out-of-view gate + reduced-motion + arbiter) as
 * the model-field reveal, so there is exactly ONE bespoke scroll in the view layer.
 * The per-surface scroll context (homepage window vs modal `.modal-body`) is resolved
 * by `nearestScrollContainer(list)`, so this is surface-agnostic — no `fraction`
 * tuning, no cap (the list is fit-content; the container scroll reaches a long list).
 */
export function buildListReveal(scrollInView: ScrollInView): (list: HTMLElement) => void {
  return (list: HTMLElement) =>
    scrollInView.request({
      axis: 'y',
      target: list,
      container: nearestScrollContainer(list),
      margin: LIST_REVEAL_MARGIN,
    });
}

interface DropdownI18n {
  ctaDisabled: string;
  ctaEnabled: string;
  openModel: string;
}

function readI18n(el: HTMLElement): DropdownI18n {
  const d = el.dataset;
  return {
    ctaDisabled: d.ctaDisabled || 'Bitte wählen Sie ein Modell',
    ctaEnabled: d.ctaEnabled || 'Ersatzteil finden',
    openModel: d.openModel || 'Modell öffnen',
  };
}

export class DropdownView extends PositionalShellView {
  readonly root: HTMLElement;
  private readonly i18n: DropdownI18n;
  private readonly container: HTMLElement;
  private readonly template: HTMLTemplateElement | null;
  private readonly navigateBtn: HTMLAnchorElement | null;
  /** The ONE external model slot under the field stack (image + catalog link, Locked Spec §1). */
  private readonly externalDetail: HTMLAnchorElement | null;
  private readonly enhancer: DropdownEnhancer;
  private readonly loadingManager = new LoadingStateManager();

  /** The shared `<select>`-SSoT field lifecycle + model-field detail mount (§5). */
  private readonly fieldFinder: FieldFinder;

  /** The vertical model-field auto-scroll machinery (one window-scoped arbiter). */
  private readonly scrollInView: ScrollInView;
  /** Ties the passive window user-scroll feeders to teardown (symmetric with ButtonColumnView). */
  private readonly listeners = new AbortController();

  /** The positional combobox fields (index = position). [0] is the SSR seed. */
  private readonly groups: HTMLElement[] = [];

  constructor(root: HTMLElement) {
    super();
    this.root = root;
    this.i18n = readI18n(root);
    this.container = root.querySelector<HTMLElement>('.datte-mf-ssr-container') ?? root;
    this.template = root.querySelector<HTMLTemplateElement>('.datte-mf-ssr-dropdown-group-template');
    this.navigateBtn = root.querySelector<HTMLAnchorElement>('#datte-mf-navigate-btn');
    this.externalDetail = this.container.querySelector<HTMLAnchorElement>('.datte-mf-external-detail');
    // The SSR surface scrolls the document, so the scroll context is the window
    // (nearestScrollContainer returns null here; resolved lazily on first request
    // so a future embedding inside a scroll element is handled transparently).
    const arbiter = new ScrollIntentArbiter();
    this.scrollInView = new ScrollInView({ arbiter });
    bindWindowScrollIntent({
      arbiter,
      scrollInView: this.scrollInView,
      signal: this.listeners.signal,
      scrollContext: null,
    });

    this.enhancer = new DropdownEnhancer({
      prefix: 'mf-ssr',
      filterParentId: false,
      // Opt into the OVERLAY-GROW open-reveal (ADR-0010 2026-06-09, supersedes D39): the
      // open list stays an absolute OVERLAY (floats over the fields below) but drops its
      // internal cap, so the document grows and the window scroll reaches a long list,
      // then we minimally scroll it into view via the shared ScrollInView. The homepage
      // scrolls the document → nearestScrollContainer resolves the window.
      dropdownReveal: { onOpen: buildListReveal(this.scrollInView) },
    });

    this.fieldFinder = new FieldFinder({
      openModelLabel: this.i18n.openModel,
      revealMargin: SCROLL_REVEAL_MARGIN,
      setLoading: (wrapper, on) => this.loadingManager.setLoading(wrapper, on),
    });
    this.fieldFinder.setEnhancer(this.enhancer);
    this.fieldFinder.setScrollInView(this.scrollInView);

    // Bind + enhance the SSR-rendered position-0 field (manufacturer).
    const seed = this.container.querySelector<HTMLElement>('.datte-mf-ssr-dropdown-group');
    if (seed) {
      this.groups.push(seed);
      this.seedPositionZeroNodes();
      this.enhanceGroup(0);
    }

    this.formatHeadline();
    this.showCta(null);
    this.bindNavigate();
  }

  // -- ModelFinderView (positional skeleton lives in PositionalShellView) ----

  select(position: Position, id: NodeId): void {
    const dropdown = this.enhancer.dropdowns.get(position);
    if (!dropdown) return;
    // `parseNodeId('')` is null, so the empty placeholder never matches (not even
    // the virtual root 0, which `Number('')` used to collide with).
    const option = Array.from(dropdown.select.options).find((o) => parseNodeId(o.value) === id);
    if (!option) return;
    dropdown.select.value = option.value;
    dropdown.input.value = option.dataset.name || option.textContent || '';
  }

  /** SSR has no horizontal track — scroll-into-view is a no-op. */
  scrollActiveIntoView(): void {}

  /**
   * No-op: the modal-open reveal affordance (D12 exception) is modal-only by
   * design (it signals the modal's scrollable inner body). The homepage SSR finder
   * is never auto-opened with a preselect and must keep D12 intact, so the
   * controller never calls this here — the no-op makes that explicit at the view.
   */
  revealSelectionOnOpen(): void {}

  /**
   * No-op: the SSR surface is width-stable (not a responsive modal that narrows
   * after a fade) and its presentation has no COLUMN-vs-DROPDOWN branch — the model
   * image always renders in the ONE external slot. `showCta` is already correct on
   * first call, so there is nothing to re-finalize after layout.
   */
  finalizePresentationAfterLayout(): void {}

  /**
   * Reveal (or clear) the model detail for a terminal leaf selection (D13) and drive
   * the standalone CTA. The model image renders in the EXTERNAL slot under the field
   * stack (Locked Spec §1/§2) — clickable to the parts catalog — and the standalone
   * `#datte-mf-navigate-btn` below it is the always-visible primary action (disabled
   * until a model is selected). The decorative hero is NEVER touched here (fixes (b)).
   */
  showCta(cta: CtaData | null): void {
    this.mountModelSlot(cta);
    this.driveNavigate(cta);
  }

  /** Populate (or clear) the ONE external model slot under the field stack (Locked Spec §1). */
  private mountModelSlot(cta: CtaData | null): void {
    if (cta && this.externalDetail) {
      this.fieldFinder.mountModelDetail(
        { detail: this.externalDetail, revealTarget: this.externalDetail },
        cta.node,
        cta.url,
        cta.live
      );
      // The image itself is the catalog link (Locked Spec §2) — un-hide for SR + pointer.
      this.externalDetail.setAttribute('aria-hidden', 'false');
    } else {
      this.fieldFinder.clearModelDetail(this.externalDetail);
      this.externalDetail?.setAttribute('aria-hidden', 'true');
    }
  }

  /** Enable/disable + href + label the standalone CTA button (always visible). */
  private driveNavigate(cta: CtaData | null): void {
    if (!this.navigateBtn) return;
    if (cta) {
      this.navigateBtn.classList.remove('datte-is-disabled');
      this.navigateBtn.textContent = this.i18n.ctaEnabled;
      this.navigateBtn.setAttribute('href', cta.url);
      this.navigateBtn.setAttribute('aria-disabled', 'false');
    } else {
      this.navigateBtn.classList.add('datte-is-disabled');
      this.navigateBtn.textContent = this.i18n.ctaDisabled;
      this.navigateBtn.setAttribute('href', '#');
      this.navigateBtn.setAttribute('aria-disabled', 'true');
    }
  }

  applyLabels(labels: ReadonlyMap<Level, LevelLabel>): void {
    this.groups.forEach((group, position) => {
      const level = this.plan[position];
      const label = level !== undefined ? labels.get(level) : undefined;
      if (!label) return;

      const labelEl = group.querySelector<HTMLElement>('.datte-mf-ssr-label');
      if (labelEl) labelEl.textContent = label.title;

      const select = group.querySelector<HTMLSelectElement>('.datte-mf-ssr-dropdown');
      if (select) this.fieldFinder.applyLabel(position, select, label);
    });
  }

  /** SSR has no geometry-dependent layout. */
  layout(): void {}

  destroy(): void {
    // Tear cloned fields back to the SSR seed BEFORE destroying the enhancer, so
    // each removed position's dropdown widget is closed/deleted on a still-live
    // enhancer (removeShell drives that). Symmetric with construction (D10).
    this.teardownShells();
    this.enhancer.destroy();
    // Remove the passive window user-scroll feeders in one call (the gate itself
    // is synchronous — no observer to disconnect).
    this.listeners.abort();
  }

  // -- Positional shell reconciliation (D10/D18 — base drives the count) ----

  protected shellCount(): number {
    return this.groups.length;
  }

  protected addShell(): void {
    if (!this.template) return;
    const fragment = this.template.content.cloneNode(true) as DocumentFragment;
    const group = fragment.querySelector<HTMLElement>('.datte-mf-ssr-dropdown-group');
    if (!group) return;
    // Insert before the external model slot (which sits above the CTA action) so the
    // stack order stays: fields → external image slot → CTA (Locked Spec §1).
    const anchor = this.externalDetail ?? this.container.querySelector('.datte-mf-ssr-action');
    this.container.insertBefore(group, anchor);
    const position = this.groups.length;
    this.groups.push(group);
    this.enhanceGroup(position);
  }

  protected removeShell(): void {
    const position = this.groups.length - 1;
    const group = this.groups.pop();
    group?.remove();
    this.dropNodesAt(position);
    this.fieldFinder.dropField(position);
  }

  /** Reset a reused field's transient state (D14, position-keyed). Delegates the ONE reset. */
  protected resetShell(position: number): void {
    const group = this.groups[position];
    const select = group?.querySelector<HTMLSelectElement>('.datte-mf-ssr-dropdown');
    if (select) this.fieldFinder.resetField(position, select);
    // The model image lives in the ONE external slot (not per-field), cleared by
    // showCta(null) on a plan change — no per-field detail to reset here.
    this.nodesAt(position).clear();
  }

  private enhanceGroup(position: number): void {
    const select = this.groups[position]?.querySelector<HTMLSelectElement>('.datte-mf-ssr-dropdown');
    if (!select) return;
    if (position === 0) select.disabled = false;
    this.fieldFinder.enhanceField(select, position, (p) => this.onDropdownChange(p));
  }

  // -- User input → controller ---------------------------------------------

  private onDropdownChange(position: number): void {
    const select = this.groups[position]?.querySelector<HTMLSelectElement>('.datte-mf-ssr-dropdown');
    if (!select) return;
    this.emitSelect(positionOf(position), this.resolveSelectedNode(position, select.value));
  }

  // -- Rendering helpers ----------------------------------------------------

  protected populatePosition(position: number, items: Node[]): void {
    const group = this.groups[position];
    const select = group?.querySelector<HTMLSelectElement>('.datte-mf-ssr-dropdown');
    if (!select) return;

    const map = this.nodesAt(position);
    items.forEach((item) => map.set(item.id, item));

    this.fieldFinder.populateField(position, select, items);
    select.disabled = items.length === 0 && position > 0;
    const dropdown = this.enhancer.dropdowns.get(position);
    if (dropdown) dropdown.input.disabled = select.disabled;
  }

  /** Build a position-0 node map from the server-rendered manufacturer options. */
  private seedPositionZeroNodes(): void {
    const select = this.groups[0]?.querySelector<HTMLSelectElement>('.datte-mf-ssr-dropdown');
    if (!select) return;
    const map = this.nodesAt(0);
    Array.from(select.options).forEach((opt) => {
      if (!opt.value) return;
      // Shared id rule (number or 22-char public id); unparsable → skip.
      const id = parseNodeId(opt.value);
      if (id === null) return;
      map.set(id, {
        id,
        parentId: null,
        name: opt.dataset.name || opt.textContent || '',
        url: opt.dataset.url || null,
        imageUrl: opt.dataset.imageUrl || null,
        level: 1,
        // NOT always true — a manufacturer whose sub-categories are not yet synced
        // from the ERP is childless (Segway live). Safe only because
        // `ModelFinderCore.onSelect` fetches whenever no column plan resolves, so
        // the seeded `true` and the real `false` converge on the same render.
        // @see ADR-0009 Decision History 2026-09-25.
        hasChildren: true,
      });
    });
  }

  // -- One-time SSR setup ---------------------------------------------------

  private formatHeadline(): void {
    const headline = this.root.querySelector<HTMLHeadingElement>('.datte-mf-ssr-headline h2');
    if (!headline) return;

    const words = (headline.textContent?.trim() || '').split(/\s+/);
    if (words.length < 6) return;

    const line1 = words.slice(0, 2).join(' ');
    const line2 = words.slice(2, 5).join(' ');
    const line3 = words.slice(5).join(' ');
    headline.innerHTML = `${line1}<br class="headline-br-desktop"> ${line2}<br class="headline-br-desktop"> ${line3}`;
  }

  private bindNavigate(): void {
    const btn = this.navigateBtn;
    if (!btn) return;
    btn.addEventListener(
      'click',
      (e) => {
        const href = btn.getAttribute('href');
        if (btn.classList.contains('datte-is-disabled') || !href || href === '#') {
          e.preventDefault();
        } else {
          btn.classList.add('is-loading'); // navigating away
        }
      },
      { signal: this.listeners.signal }
    );
  }
}
